import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { DataManager, Query } from '@syncfusion/ej2-data';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { fromPromise } from 'rxjs/internal-compatibility';
import { map, tap } from 'rxjs/operators';
import { JwtUrlAdaptor } from '../../adaptors/jwt-url-adaptor';
import { EntiteModel } from '../../models/entites/entite.model';
import { ToastSuccesModel } from '../../models/toasts/toast-succes.model';
import { ValidatorsBuilderService } from '../../validators/validators-with-message/eparcours-validators/validators-builder.service';
import { ConfigService } from '../config/config.service';
import { NotificationToastService } from '../notifications/notification-toast.service';
import { LocalStorageTokenService } from '../utilisateurs/local-storage-token.service';

@Injectable({
  providedIn: 'root',
})
export class EntitesService {
  private readonly MAX_PAGE_SIZE = 10;
  idEntiteSelectionneeEvent: BehaviorSubject<number> = new BehaviorSubject<number>(null);
  _entites: EntiteModel[] = null;
  entitesUtilisateur: Observable<EntiteModel[]> = new Observable<EntiteModel[]>(observer => {
    if (this._entites == null) {
      const s = this.getUtilisateursEntites().subscribe(v => {
        this._entites = v;
        observer.next(this._entites);
        s.unsubscribe();
      });
    } else {
      observer.next(this._entites);
    }
  });
  private readonly baseUrlApi = this.configService.config.urlApi;;

  constructor(
    private client: HttpClient,
    private notificationToastService: NotificationToastService,
    private jwtUrlAdaptor: JwtUrlAdaptor,
    private validatorsService: ValidatorsBuilderService,
    private configService: ConfigService,
    private localStorageTokenService: LocalStorageTokenService,
  ) {}

  buildDataManagerGetEntites(): DataManager {
    return new DataManager({
      url: `${this.baseUrlApi}/api/e/entites/getEntites/`,
      adaptor: this.jwtUrlAdaptor,
    });
  }

  getAllEntites(): Observable<EntiteModel[]> {
    return fromPromise(
      this.buildDataManagerGetEntites().executeQuery(new Query().page(1, this.MAX_PAGE_SIZE).skip(1)),
    ).pipe(map(r => r['result']['result']));
  }

  getEntiteFromId(idEntite: number): Observable<EntiteModel> {
    if(idEntite){
      return this.client.get<EntiteModel>(`${this.baseUrlApi}/api/e/entites/getEntiteById/${idEntite}`);
    } else {
      return of(null)
    }
  }

  addEntite(entite: EntiteModel): Observable<any> {
    return this.client
      .put<void>(`${this.baseUrlApi}/api/e/entites/addEntite`, entite)
      .pipe(tap(() => this.notificationToastService.push(new ToastSuccesModel("Création de l'entité réussie."))));
  }

  getIdEntiteParDefaut(): Observable<number> {
    return this.getUtilisateursEntites().pipe(
      map(entites => {
        const entitesIds = [];
        entites.forEach(item => {
          entitesIds.push(item.id);
        });
        return Math.min(...entitesIds);
      }),
    );
  }

  updateEntite(idEntite: number, entite: EntiteModel): Observable<void> {
    return this.client
      .post<void>(`${this.baseUrlApi}/api/e/entites/updateEntite/${idEntite}`, entite)
      .pipe(tap(() => this.notificationToastService.push(new ToastSuccesModel("Modification de l'entité réussie."))));
  }

  desactivateEntite(entite: EntiteModel) {
    return this.client
      .post<void>(`${this.baseUrlApi}/api/e/entites/updateEntite/${entite.id}`, entite)
      .pipe(tap(() => this.notificationToastService.push(new ToastSuccesModel('Entité correctement désactivée.'))));
  }

  reactivateEntite(entite: EntiteModel) {
    return this.client
      .post<void>(`${this.baseUrlApi}/api/e/entites/updateEntite/${entite.id}`, entite)
      .pipe(tap(() => this.notificationToastService.push(new ToastSuccesModel('Entité correctement activée'))));
  }

  public getUtilisateursEntites(): Observable<EntiteModel[]> {
    return this.client.get<EntiteModel[]>(`${this.baseUrlApi}/api/u/utilisateur/getUtilisateurEntites`);
  }

  public getUtilisateursEntitesGerables(): Observable<EntiteModel[]> {
    return this.client.get<EntiteModel[]>(`${this.baseUrlApi}/api/u/utilisateur/getUtilisateurEntitesNew`);
  }

  public getUtilisateurEntitesAxes(): Observable<EntiteModel[]> {
    return this.client.get<EntiteModel[]>(`${this.baseUrlApi}/api/u/utilisateur/GetUtilisateurEntitesGestionAnalytique`);
  }

  setIdEntiteSelected(idEntiteSelected: number) {
    this.idEntiteSelectionneeEvent.next(idEntiteSelected);
  }

  getSelectedEntiteDeTravail(): Observable<EntiteModel> {
    return this.getEntiteFromId(this.idEntiteSelectionneeEvent.value);
  }

  getSelectedIdEntiteDeTravail(): Observable<number> {
    return of(this.idEntiteSelectionneeEvent.value);
  }

  getSelectedIdEntite(): number {
    return this.idEntiteSelectionneeEvent.value;
  }

  /**
   * Crée un formControl de sélecteur d’entité avec une valeur par défaut, choisie selon l’algorithme suivant :
   * - Premier choix : l’entité de travail sélectionnée si elle est définie et qu’elle est autorisée par le paramètre de filtre
   * - Sinon : la première des entités de travail autorisées par le paramètre de filtre
   * @param filterValue : filtre d’entités = liste parmi laquelle doit se trouver l’entité à sélectionner par défaut.
   */
  createMultiEntiteSelectionControlWithDefaultValue(filterValue: number[] = null): Observable<UntypedFormControl> {
    const getFirstValue = entites => entites.sort((left, right) => left.id - right.id)[0].id;
    const isAllowed = value => (filterValue != null && value != null ? filterValue.indexOf(value) >= 0 : true);
    let valeurDefaut: number[] = null;
    if (this.idEntiteSelectionneeEvent.value != null && isAllowed(this.idEntiteSelectionneeEvent.value)) {
      valeurDefaut = [this.idEntiteSelectionneeEvent.value];
    }
    if (valeurDefaut == null) {
      return this.getUtilisateursEntitesGerables().pipe(
        map(entites => entites.filter(e => e.actif && e.isGerableUtilisateur)),
        map(entites => {
          valeurDefaut = [getFirstValue(entites)];
          return new UntypedFormControl(valeurDefaut, this.validatorsService.required());
        }),
      );
    } else {
      return of(new UntypedFormControl(valeurDefaut, this.validatorsService.required()));
    }
  }

  /**
   * Crée un formControl de sélecteur d’entité avec une valeur par défaut, choisie selon l’algorithme suivant :
   * - Premier choix : l’entité de travail sélectionnée si elle est définie et qu’elle est autorisée par le paramètre de filtre
   * - Sinon : la premier des entités de travail autorisées par le paramètre de filtre
   * @param filterValue : filtre d’entités = liste parmi laquelle doit se trouver l’entité à sélectionner par défaut.
   */
  createSingleEntiteSelectionControlWithDefaultValue(filterValue: number[] = null): Observable<UntypedFormControl> {
    const getFirstValue = entites => entites.sort((left, right) => left.id - right.id)[0].id;
    const isAllowed = value => (filterValue != null && value != null ? filterValue.indexOf(value) >= 0 : true);
    let valeurDefaut = null;
    if (!this.idEntiteSelectionneeEvent.value != null && isAllowed(this.idEntiteSelectionneeEvent.value)) {
      valeurDefaut = this.idEntiteSelectionneeEvent.value;
    }
    if (valeurDefaut == null) {
      return this.getUtilisateursEntites().pipe(
        map(entites => entites.filter(isAllowed)),
        map(entites => {
          valeurDefaut = getFirstValue(entites);
          return new UntypedFormControl(valeurDefaut);
        }),
      );
    } else {
      return of(new UntypedFormControl(valeurDefaut));
    }
  }


  getEntiteParDefaut(filterValue: number[] = null): Observable<number> {
    const getFirstValue = entites => entites.sort((left, right) => left.id - right.id)[0].id;
    const isAllowed = value => (filterValue != null && value != null ? filterValue.indexOf(value) >= 0 : true);
    let valeurDefaut = null;
    if (!this.idEntiteSelectionneeEvent.value != null && isAllowed(this.idEntiteSelectionneeEvent.value)) {
      valeurDefaut = this.idEntiteSelectionneeEvent.value;
    }
    if (valeurDefaut == null) {
      return this.getUtilisateursEntites().pipe(
        map(entites => entites.filter(isAllowed)),
        map(entites => {
          valeurDefaut = getFirstValue(entites);
          return valeurDefaut;
        }),
      );
    } else {
      return of(valeurDefaut);
    }
  }

  public getLabelEntites(data){
    return data.EntitesLibelles || data.LibelleEntite || data.entitesLibelles;
  }

  public getEntiteCodesThree(data) {
    const entiteCodes = this.getEntiteCodes(data);
    if (entiteCodes.length > 3) {
        return entiteCodes.slice(0, 3).concat('...');
    }
    return entiteCodes;
  }

  public getEntiteCodes(data: any): string[] {
    if(data.entitesCodes){
      return data.entitesCodes.split(', ')
    }
    if(data.EntitesCodes){
      return data.EntitesCodes.split(', ');
    }
    if(data.CodeEntite){
      return data.CodeEntite.split(', ');
    }
    return [];
  }
}
